import React, { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  GroupOutlined,
  MailOutline,
  RateReviewOutlined,
  ShareOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { upperFirst } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import {
  generalSnackBarAtom,
  useAcceptShareAccess,
  useAuthContext,
  useFriendsList,
  useReferralLink,
} from '../../../../../data-access';
import { FriendsFeedbackForm } from '../../../../SideBar/SettingsModal/ManageFriendsSettings/FriendsFeedbackForm';
import './FriendsSection.scss';

const FriendsSection: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [referralCode, setReferralCode] = useState('');
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [isCheckingReferral, setIsCheckingReferral] = useState(false);
  const [isCopyingInvite, setIsCopyingInvite] = useState(false);
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);

  const {
    friendsList,
    isLoading: isFriendsLoading,
    isFetching: isFriendsFetching,
  } = useFriendsList({ userId: user.userId });

  const { acceptShareAccess, isPending: isAcceptingInvite } = useAcceptShareAccess();
  const { generateInviteLink, isGenerating } = useReferralLink();

  const isLoadingFriendsList = useMemo(
    () => isFriendsLoading || isFriendsFetching,
    [isFriendsLoading, isFriendsFetching]
  );

  const isSubmittingReferral = isAcceptingInvite || isCheckingReferral;
  const isInviteCodeGenerating = isGenerating && !inviteCode;

  const showReferralToast = useCallback(
    (title: string, message: string) => {
      setGeneralSnackbar(`${title} – ${message}`);
      setTimeout(() => {
        setGeneralSnackbar('');
      }, 6000);
    },
    [setGeneralSnackbar]
  );

  const ensureInviteCode = useCallback(async (): Promise<string | null> => {
    if (inviteCode) {
      return inviteCode;
    }

    if (!user?.userId) {
      return null;
    }

    const { referralCode: generatedCode, error } = await generateInviteLink(user.userId);

    if (error || !generatedCode) {
      return null;
    }

    setInviteCode(generatedCode);
    return generatedCode;
  }, [generateInviteLink, inviteCode, user?.userId]);

  useEffect(() => {
    if (!inviteCode && user?.userId) {
      void ensureInviteCode();
    }
  }, [ensureInviteCode, inviteCode, user?.userId]);

  const handleAcceptInvite = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedReferralCode = referralCode.trim();

      if (!trimmedReferralCode) {
        showReferralToast(
          t('settings.friends.failedToAcceptReferral'),
          t('settings.friends.enterInviteCode')
        );
        return;
      }

      if (!user?.userId) {
        showReferralToast(
          t('settings.friends.failedToAcceptReferral'),
          t('settings.friends.signInToManageFriends')
        );
        return;
      }

      setIsCheckingReferral(true);

      try {
        const generatedCode = await ensureInviteCode();

        if (!generatedCode) {
          showReferralToast(
            t('settings.friends.failedToAcceptReferral'),
            t('settings.friends.doubleCheckCode')
          );
          return;
        }

        if (trimmedReferralCode === generatedCode) {
          showReferralToast(
            t('settings.friends.failedToAcceptReferral'),
            t('settings.friends.ownReferralCode')
          );
          return;
        }

        await acceptShareAccess({
          userId: user.userId,
          referralCode: trimmedReferralCode,
        });

        setReferralCode('');
        showReferralToast(
          t('settings.friends.referralAccepted'),
          t('settings.friends.sharedAccessAdded')
        );
      } catch {
        showReferralToast(
          t('settings.friends.failedToAcceptReferral'),
          t('settings.friends.doubleCheckCode')
        );
      } finally {
        setIsCheckingReferral(false);
      }
    },
    [acceptShareAccess, ensureInviteCode, referralCode, showReferralToast, t, user?.userId]
  );

  const handleCopyInviteCode = useCallback(async () => {
    setIsCopyingInvite(true);

    try {
      const code = await ensureInviteCode();

      if (!code) {
        showReferralToast(
          t('settings.friends.unableToGenerateCode'),
          t('settings.common.tryAgainLater')
        );
        return;
      }

      const textToBeCopied = t('settings.friends.inviteClipboardText', { code });

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToBeCopied);
      }

      showReferralToast(
        t('settings.friends.inviteCodeCopied'),
        t('settings.friends.shareCodeToConnect')
      );
    } catch {
      showReferralToast(t('settings.friends.unableToCopyCode'), t('settings.common.tryAgainLater'));
    } finally {
      setIsCopyingInvite(false);
    }
  }, [ensureInviteCode, showReferralToast, t]);

  const safeFriendsList = friendsList || [];
  const isCopyDisabled = isInviteCodeGenerating || !inviteCode || isCopyingInvite;

  return (
    <Stack gap={2} className="friends-section">
      <Typography variant="h3" className="friends-section__title">
        {t('settings.nav.friends')}
      </Typography>

      <div className="friends-section__block">
        <Stack className="friends-section__list" gap={2}>
          <Stack
            id="connectedFriends"
            gap={2}
            className="friends-section__list-item-wrapper"
            sx={{ scrollMarginTop: '12px' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              className="friends-section__list-item"
            >
              <GroupOutlined className="friends-section__list-item-icon" />
              <Typography variant="body1" className="friends-section__list-item-title">
                {t('settings.nav.connectedFriends')}
              </Typography>
              {isLoadingFriendsList && (
                <CircularProgress size={16} className="friends-section__loading" />
              )}
            </Stack>

            <Stack className="friends-section__friends-list" gap={1.5}>
              {isLoadingFriendsList ? (
                <Typography variant="body2" className="friends-section__placeholder">
                  {t('settings.friends.loadingConnections')}
                </Typography>
              ) : safeFriendsList.length > 0 ? (
                safeFriendsList.map((friend) => (
                  <Stack key={friend.userId} direction="row" alignItems="center" gap={1.5}>
                    {friend.photoUrl ? (
                      <Avatar
                        src={friend.photoUrl}
                        alt={friend.name || friend.email || t('settings.friends.friendAlt')}
                        sx={{ width: 32, height: 32 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'var(--color-gray-base, #b5b5bc)',
                          color: 'var(--color-black, #131314)',
                          fontSize: '0.875rem',
                        }}
                      >
                        {upperFirst(friend.name?.slice(0, 1) ?? friend.email?.slice(0, 1) ?? '')}
                      </Avatar>
                    )}

                    <Typography variant="body2" className="friends-section__friend-name">
                      {friend.name || friend.email || t('settings.friends.unknownFriend')}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2" className="friends-section__placeholder">
                  {t('settings.friends.noConnectedFriends')}
                </Typography>
              )}
            </Stack>

            <Divider className="friends-section__list-item-divider" />
          </Stack>

          <Stack
            id="acceptInvite"
            gap={2}
            className="friends-section__list-item-wrapper"
            sx={{ scrollMarginTop: '12px' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              className="friends-section__list-item"
            >
              <MailOutline className="friends-section__list-item-icon" />
              <Typography variant="body1" className="friends-section__list-item-title">
                {t('settings.nav.acceptInvite')}
              </Typography>
            </Stack>

            <form className="friends-section__invite-form" onSubmit={handleAcceptInvite}>
              <TextField
                size="small"
                label={t('settings.friends.inviteCode')}
                placeholder="XXXXXXXX"
                value={referralCode}
                onChange={(event) => setReferralCode(event.target.value)}
                className="friends-section__invite-input"
                disabled={isSubmittingReferral}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LoadingButton
                        loading={isSubmittingReferral}
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={isSubmittingReferral || !referralCode.trim()}
                        className="friends-section__invite-submit"
                      >
                        {t('settings.common.submit')}
                      </LoadingButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>

            <Divider className="friends-section__list-item-divider" />
          </Stack>

          <Stack
            id="shareInvite"
            gap={2}
            className="friends-section__list-item-wrapper"
            sx={{ scrollMarginTop: '12px' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              className="friends-section__list-item"
            >
              <ShareOutlined className="friends-section__list-item-icon" />
              <Typography variant="body1" className="friends-section__list-item-title">
                {t('settings.nav.shareInvite')}
              </Typography>
            </Stack>

            <div className="friends-section__share-code">
              <Typography variant="body2" className="friends-section__share-code-value">
                {isInviteCodeGenerating ? t('settings.friends.generatingInviteCode') : inviteCode}
              </Typography>

              <IconButton
                size="small"
                className="friends-section__share-copy"
                onClick={handleCopyInviteCode}
                disabled={isCopyDisabled}
                aria-label={t('settings.friends.copyInviteCode')}
              >
                {isCopyDisabled ? (
                  <CircularProgress size={14} />
                ) : (
                  <ContentCopy fontSize="inherit" />
                )}
              </IconButton>
            </div>

            <Typography variant="body2" className="friends-section__share-note">
              {t('settings.friends.shareNotePrefix')}{' '}
              <strong>{t('settings.friends.shareNoteDiscount')}</strong>{' '}
              {t('settings.friends.shareNoteSuffix')}
            </Typography>

            <Divider className="friends-section__list-item-divider" />
          </Stack>

          <Stack
            id="shareFeedback"
            gap={2}
            className="friends-section__list-item-wrapper"
            sx={{ scrollMarginTop: '12px' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              className="friends-section__list-item"
            >
              <RateReviewOutlined className="friends-section__list-item-icon" />
              <Typography variant="body1" className="friends-section__list-item-title">
                {t('settings.nav.shareFeedback')}
              </Typography>
            </Stack>

            <div className="friends-section__feedback-form">
              <FriendsFeedbackForm className="friends-section__feedback-form-content" />
            </div>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

export default FriendsSection;
